/* =========================================================
   drippy — interaction layer for the Shopify theme
   GSAP 3.13 (ScrollTrigger, SplitText; Draggable + Inertia load on desktop only)
   Each section boots from its [data-drippy] root and cleans up
   after itself, so everything also works in the theme editor.
   ========================================================= */

const root = document.documentElement;
root.classList.add('dr-booted'); // the hero's inline failsafe stands down once this module runs
const $ = (s, el = document) => el.querySelector(s);
const $$ = (s, el = document) => [...el.querySelectorAll(s)];
const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = matchMedia('(hover: hover) and (pointer: fine)').matches;
const designMode = Boolean(window.Shopify && window.Shopify.designMode);
const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
document.addEventListener('touchstart', () => {}, { passive: true }); // lets iOS show :active press states
const idle = (fn) => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 900 }) : setTimeout(fn, 300));

/* Horizon scrolls `.page-wrapper` on desktop (≥990px, so the cart drawer can squeeze the page)
   and the window on phones. Every scroll animation follows whichever one is live. */
const squeeze = matchMedia('(min-width: 990px)');
const scrollEl = () => (squeeze.matches && document.querySelector('.page-wrapper')) || window;
const scrollTopOf = (sc) => (sc === window ? window.scrollY : sc.scrollTop);

/* While the loader covers the page, the page under it is inert (the hero's inline script sets that) */
function unlockPage() {
  root.classList.remove('dr-loading');
  const page = document.querySelector('.page-wrapper');
  if (page) page.inert = false;
}

function dropLoader() {
  unlockPage();
  $$('[data-dr-loader]').forEach((el) => el.remove());
}

const { gsap, ScrollTrigger, SplitText } = window;

/* Standard storefront events keep Horizon's cart drawer + counts in sync */
let CartEvents = null;
try {
  CartEvents = (await import('@shopify/events')).CartLinesUpdateEvent;
} catch (e) {
  CartEvents = null;
}

if (!gsap || !ScrollTrigger) {
  dropLoader();
  root.classList.remove('dr-pre');
} else {
  gsap.registerPlugin(...[ScrollTrigger, SplitText].filter(Boolean));
  boot().catch(() => { dropLoader(); root.classList.remove('dr-pre'); });
}

/* ---------------------------------------------------------
   DRIP GEOMETRY — shared by loader, hero pour and section edges
   --------------------------------------------------------- */
function rng(seed) {
  return () => {
    seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeDrips(width, { seed = 1, gap = [46, 130], r = [7, 17], len = [14, 80] } = {}) {
  const rand = rng(seed);
  const drips = [];
  let x = 14 + rand() * 40;
  while (x < width - 14) {
    const rr = r[0] + rand() * (r[1] - r[0]);
    const long = rand() > 0.72;
    drips.push({ x, r: rr, len: long ? len[1] * (0.9 + rand() * 0.6) : len[0] + rand() * (len[1] - len[0]) * 0.6 });
    x += gap[0] + rand() * (gap[1] - gap[0]);
  }
  return drips;
}

function dripPath(width, drips, band, stretch = 1) {
  let d = `M0,0 H${width} V${band}`;
  for (let i = drips.length - 1; i >= 0; i--) {
    const { x, r } = drips[i];
    const L = Math.max(r * 0.6, drips[i].len * stretch);
    const f = Math.min(r * 0.9, 9);
    d += ` L${x + r + f},${band} Q${x + r},${band} ${x + r},${band + f} L${x + r},${band + L} A${r},${r} 0 0 1 ${x - r},${band + L} L${x - r},${band + f} Q${x - r},${band} ${x - r - f},${band}`;
  }
  return `${d} L0,${band} Z`;
}

function sizeSvg(svg, w, h) {
  svg.setAttribute('width', w);
  svg.setAttribute('height', h);
  svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
}

/* Section edges: a band plus a row of drips. Each drip is its own small <svg>, so its ooze is a
   transform on an element the compositor can run, not a repaint of one big svg every frame. */
function buildEdges(scope = document) {
  const edges = $$('.dr-drip-edge', scope);
  const widths = edges.map((edge) => edge.offsetWidth || innerWidth); // all reads before any write
  edges.forEach((edge, k) => {
    const w = widths[k];
    const seed = Number(edge.dataset.seed) || 11 + k * 7;
    const drips = makeDrips(w, { seed, len: [12, 70] });
    const parts = drips.map((dp, i) => {
      const f = Math.min(dp.r * 0.9, 9);
      const dw = 2 * (dp.r + f), dh = dp.len + 1 + dp.r;
      const t = (2.2 + ((i * 37) % 17) / 10).toFixed(2);
      return `<svg class="dr-d" width="${dw.toFixed(1)}" height="${dh.toFixed(1)}" viewBox="0 0 ${dw} ${dh}" style="left:${(dp.x - dp.r - f).toFixed(1)}px;--t:${t}s;animation-delay:-${(i % 5) * 0.6}s"><path d="M0,0 Q${f},0 ${f},${f} L${f},${dp.len + 1} A${dp.r},${dp.r} 0 0 0 ${f + 2 * dp.r},${dp.len + 1} L${f + 2 * dp.r},${f} Q${f + 2 * dp.r},0 ${dw},0 Z"/></svg>`;
    }).join('');
    edge.style.color = edge.dataset.fill || '#F6E6C2';
    edge.setAttribute('aria-hidden', 'true');
    edge.innerHTML = `<i class="dr-drip-edge__band"></i>${parts}`;
  });
}

/* ---------------------------------------------------------
   SHARED HELPERS
   --------------------------------------------------------- */
/* Endless loops register here so they can sleep while their section is off screen. */
function loop(el, anim) {
  const root = el?.closest?.('[data-drippy]') || el;
  if (root && anim) (root._drLoops ||= []).push(anim);
  return anim;
}

function watchVisibility(root) {
  if (!('IntersectionObserver' in window)) return null;
  const io = new IntersectionObserver(([entry]) => {
    const on = entry.isIntersecting;
    root.classList.toggle('dr-offscreen', !on);
    (root._drLoops || []).forEach((a) => (on ? a.resume() : a.pause()));
  }, { rootMargin: '200px 0px' });
  io.observe(root);
  return io;
}

const splitChars = (el) => new SplitText(el, { type: 'words,chars', charsClass: 'sch' }).chars;

function money(cents) {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(cents / 100);
  } catch (e) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

/* ---------------------------------------------------------
   CART — real Shopify cart, Horizon's drawer
   --------------------------------------------------------- */
const cartButton = () => $('[data-dr-cart-toggle]');
const cartDrawer = () => document.querySelector('theme-drawer#cart-drawer');

function setCartCount(n) {
  if (typeof n !== 'number') return;
  $$('[data-dr-cart-count]').forEach((b) => (b.textContent = n));
}

let lastBump = 0;
function bumpCart() {
  const btn = cartButton();
  if (!btn || reduce || performance.now() - lastBump < 900) return;
  lastBump = performance.now();
  gsap.fromTo(btn, { scale: 1 }, { scale: 1.16, duration: 0.14, ease: 'power2.out', yoyo: true, repeat: 1, onComplete: () => gsap.set(btn, { clearProps: 'transform' }) });
}

document.addEventListener('shopify:cart:lines-update', (event) => {
  event.promise?.then(({ cart }) => {
    setCartCount(cart?.totalQuantity);
    bumpCart();
  }).catch(() => {});
});

window.addEventListener('pageshow', (e) => {
  if (!e.persisted) return;
  fetch(`${shopRoot}cart.js`, { headers: { Accept: 'application/json' } })
    .then((r) => r.json())
    .then((c) => setCartCount(c.item_count))
    .catch(() => {});
});

document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-dr-cart-toggle]');
  if (!trigger) return;
  const drawer = cartDrawer();
  if (drawer && typeof drawer.toggle === 'function') {
    // Stop this click here and open on the next frame, otherwise the same click reaches
    // Horizon's click-outside handler and closes the drawer the moment it opens.
    e.preventDefault();
    e.stopPropagation();
    requestAnimationFrame(() => drawer.toggle());
  }
});

// One jar (src string) or the whole gang (fromEl and src as matching lists, from "Get all 3").
function flyToCart(fromEl, src) {
  const target = cartButton();
  const items = Array.isArray(fromEl)
    ? fromEl.map((el, i) => ({ el, src: src[i] })).filter((it) => it.el && it.src)
    : [{ el: fromEl, src }].filter((it) => it.el && it.src);
  if (reduce || !target || !items.length) return Promise.resolve();
  const header = $('.dr-header');
  const wasHidden = header?.classList.contains('is-hidden');
  header?.classList.remove('is-hidden'); // it slides back in while the jars fly
  const b = target.getBoundingClientRect();
  const slide = wasHidden ? header.offsetHeight : 0; // the pill is still translated up when measured
  const tx = b.left + b.width / 2 - 45, ty = b.top + slide + b.height / 2 - 45;
  const gang = items.length > 1;

  const tl = gsap.timeline();
  // the gang: each jar on its card squashes and hops, a quick wave left to right, before they all leap
  const hop = gang ? 0.42 : 0;
  if (gang) {
    items.forEach(({ el }, i) => {
      gsap.timeline({ delay: i * 0.07 })
        .to(el, { scaleX: 1.12, scaleY: 0.84, transformOrigin: '50% 100%', duration: 0.12, ease: 'power2.out' })
        .to(el, { scaleX: 0.94, scaleY: 1.1, y: -14, duration: 0.16, ease: 'power2.out' })
        .to(el, { scaleX: 1, scaleY: 1, y: 0, duration: 0.45, ease: 'elastic.out(1, 0.45)', clearProps: 'transform' });
    });
  }

  items.forEach(({ el, src: img }, i) => {
    const a = el.getBoundingClientRect();
    const fly = document.createElement('img');
    fly.src = img;
    fly.alt = '';
    fly.className = 'dr-fly';
    document.body.appendChild(fly);
    // jars on cards below the fold leap up from the bottom edge
    const sx = a.left + a.width / 2 - 45, sy = Math.min(a.top + a.height / 2 - 45, innerHeight - 70);
    const apex = Math.max(12, Math.min(sy, ty) - (gang ? 70 + i * 45 : 60)); // each its own arc, all on screen
    const at = hop + i * 0.13;
    const spin = gang ? (i % 2 ? -1 : 1) * (360 + i * 180) : 360;
    gsap.set(fly, { x: sx, y: sy, scale: 1.4, autoAlpha: gang ? 0 : 1 });
    tl.set(fly, { autoAlpha: 1 }, at)
      .to(fly, { x: tx, duration: 0.8, ease: 'power1.inOut' }, at)
      .to(fly, { y: apex, duration: 0.38, ease: 'power2.out' }, at)
      .to(fly, { y: ty, duration: 0.42, ease: 'power2.in' }, at + 0.38)
      .to(fly, { scale: 0.35, rotation: spin, duration: 0.8, ease: 'power1.in' }, at)
      .add(() => {
        fly.remove();
        if (gang && i < items.length - 1) gsap.fromTo(target, { scale: 1 }, { scale: 1.08, duration: 0.1, ease: 'power2.out', yoyo: true, repeat: 1 });
      }, at + 0.8);
  });

  return new Promise((resolve) => {
    tl.eventCallback('onComplete', () => {
      lastBump = 0;
      bumpCart();
      if (!gang) { resolve(); return; }
      plusBadge(target, items.length, slide);
      setTimeout(resolve, 480); // let the "+3" land before the cart drawer slides over it
    });
  });
}

// "+3" pops out of the cart pill when the gang lands
function plusBadge(target, n, slide = 0) {
  const r = target.getBoundingClientRect();
  const tag = document.createElement('span');
  tag.className = 'dr-plus';
  tag.textContent = `+${n}`;
  tag.setAttribute('aria-hidden', 'true');
  document.body.appendChild(tag);
  gsap.set(tag, { x: r.left + r.width / 2, y: r.bottom + slide - 6, xPercent: -50, scale: 0.4, autoAlpha: 0 });
  gsap.timeline({ onComplete: () => tag.remove() })
    .to(tag, { autoAlpha: 1, scale: 1, y: '+=18', rotation: -8, duration: 0.35, ease: 'back.out(3)' })
    .to(tag, { autoAlpha: 0, y: '+=10', duration: 0.3, ease: 'power1.in' }, 0.95);
}

/* Which button sold the jar: "Added from" on the order (Orders › order › Additional details), e.g. "hero, cards".
   A cart attribute, not a line property, so the same jar added from two places stays one cart line. */
const SRC_KEY = 'Added from';
async function noteSource(from, cart) {
  if (!from) return;
  try {
    cart = cart || await fetch(`${shopRoot}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const prev = cart.attributes?.[SRC_KEY];
    await fetch(`${shopRoot}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [SRC_KEY]: (prev ? `${prev}, ${from}` : from).slice(-200) } }),
    });
  } catch (err) { /* tracking only */ }
}

// variantIds: one id or a list (the "Get all 3" button); from: hero | cards | bundle | drawer
async function addToCart(variantIds, fromEl, src, from) {
  const ids = [].concat(variantIds).map(Number).filter(Boolean);
  if (!ids.length) return false;
  const sectionIds = $$('cart-items-component').map((c) => c.dataset.sectionId).filter(Boolean);
  const request = fetch(`${shopRoot}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      items: ids.map((id) => ({ id, quantity: 1 })),
      ...(sectionIds.length ? { sections: sectionIds.join(','), sections_url: location.pathname } : {}),
    }),
  }).then((r) => r.json());

  await flyToCart(fromEl, src);

  if (!CartEvents) {
    const res = await request.catch(() => null);
    if (res && !res.status) {
      await noteSource(from);
      const drawer = cartDrawer();
      if (drawer) window.location.reload();
      else window.location.assign(`${shopRoot}cart`);
    }
    return Boolean(res && !res.status);
  }

  const deferred = CartEvents.createPromise();
  (fromEl || document).dispatchEvent(new CartEvents({
    action: 'add',
    context: 'product',
    lines: ids.map((id) => ({ merchandiseId: String(id), quantity: 1 })),
    promise: deferred.promise,
  }));

  try {
    const res = await request;
    const cart = await fetch(`${shopRoot}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    deferred.resolve({
      cart: CartEvents.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'drippy', sections: res.sections, didError: Boolean(res.status), itemCount: ids.length },
    });
    if (res.status) console.warn('[drippy] add to cart failed:', res.description || res.message);
    else noteSource(from, cart);
    const drawer = cartDrawer();
    if (!res.status && drawer && !drawer.isOpen && !document.querySelector('cart-drawer-component[auto-open]')) drawer.open();
    return !res.status;
  } catch (err) {
    deferred.reject(err);
    return false;
  }
}

/* ---------------------------------------------------------
   CURSOR (fine pointers only)
   --------------------------------------------------------- */
function initCursor() {
  const cursor = $('.dr-cursor');
  if (!cursor || !finePointer || reduce || cursor._dr) return;
  cursor._dr = true;
  const label = $('.dr-cursor__label', cursor);
  const cx = gsap.quickTo(cursor, 'x', { duration: 0.35, ease: 'power3' });
  const cy = gsap.quickTo(cursor, 'y', { duration: 0.35, ease: 'power3' });
  addEventListener('pointermove', (e) => { cx(e.clientX); cy(e.clientY); cursor.classList.add('is-on'); }, { passive: true });
  document.addEventListener('pointerleave', () => cursor.classList.remove('is-on'));
  document.addEventListener('pointerover', (e) => {
    const t = e.target.closest('[data-cursor]');
    if (t && t.dataset.cursor) { label.textContent = t.dataset.cursor; cursor.classList.add('is-label'); }
    else cursor.classList.remove('is-label');
  });
}

/* ---------------------------------------------------------
   HEADER
   --------------------------------------------------------- */
function scrollToHash(hash, { instant = false } = {}) {
  const target = hash === '#top' || hash === '#' ? null : document.getElementById(hash.slice(1));
  if (hash.length > 1 && hash !== '#top' && !target) return false;
  const sc = scrollEl();
  const offset = sc === window ? 0 : sc.getBoundingClientRect().top;
  let y = target ? target.getBoundingClientRect().top - offset + scrollTopOf(sc) : 0;
  // the desktop jar story is scroll-scrubbed: land near the end, not on its empty first frame
  if (target?.classList.contains('dr-jar') && !reduce && matchMedia('(min-width: 961px)').matches) y += (target.offsetHeight - innerHeight) * 0.85;
  sc.scrollTo({ top: y, behavior: reduce || instant ? 'auto' : 'smooth' });
  if (target) { // keyboard and screen-reader users continue from the section they jumped to
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }
  return true;
}

function initHeader(el) {
  const overlay = el.dataset.overlay === 'true';
  const setH = () => root.style.setProperty('--dr-header-h', `${el.offsetHeight}px`);
  setH();
  const ro = new ResizeObserver(setH);
  ro.observe(el);
  // the ticker loops in CSS (drippy.css: dr-marquee)

  const menuOpen = () => document.body.classList.contains('dr-menu-open');
  const hero = $('.dr-hero');
  // Hide on scroll down / show on scroll up, but only after 12px of travel in one direction
  // (thumb wobble no longer pops it) and only once the page has scrolled past `minY`.
  let lastDir = 0, turnY = 0;
  const autoHide = (self, minY) => {
    const y = self.scroll();
    if (self.direction !== lastDir) { lastDir = self.direction; turnY = y; }
    if (menuOpen() || y < minY) { el.classList.remove('is-hidden'); return; }
    if (Math.abs(y - turnY) >= 12) el.classList.toggle('is-hidden', self.direction === 1);
  };
  if (overlay && hero) {
    ScrollTrigger.create({
      start: () => hero.offsetHeight - el.offsetHeight,
      end: 'max',
      onToggle: (self) => el.classList.toggle('is-solid', self.isActive),
      onUpdate: (self) => autoHide(self, hero.offsetHeight + 300),
    });
  } else if (!designMode) {
    ScrollTrigger.create({
      start: 0,
      end: 'max',
      onUpdate: (self) => autoHide(self, 240),
    });
  }

  const burger = $('[data-dr-burger]', el);
  const links = $('.dr-nav__links', el);
  const phoneNav = matchMedia('(max-width: 960px)');
  // the phone menu's links are clipped away when closed: keep them out of the Tab order then
  const syncInert = () => { if (links) links.inert = phoneNav.matches && !menuOpen(); };
  const setMenu = (open) => {
    burger?.setAttribute('aria-expanded', open);
    document.body.classList.toggle('dr-menu-open', open);
    document.body.style.overflow = open ? 'hidden' : '';
    syncInert();
    $$('main, .dr-footer').forEach((n) => (n.inert = open)); // nothing behind the open menu is reachable
    if (open) links?.querySelector('a')?.focus();
  };
  const onBurger = () => setMenu(burger.getAttribute('aria-expanded') !== 'true');
  const onEsc = (e) => { if (e.key === 'Escape' && menuOpen()) { setMenu(false); burger?.focus(); } };
  burger?.addEventListener('click', onBurger);
  document.addEventListener('keydown', onEsc);
  phoneNav.addEventListener('change', syncInert);
  syncInert();

  const onNav = (e) => {
    const a = e.target.closest('a[data-dr-scroll]');
    if (!a) return;
    const url = new URL(a.href, location.href);
    if (url.pathname === location.pathname && (url.hash || url.pathname === '/')) {
      if (scrollToHash(url.hash || '#top')) e.preventDefault();
    }
    if (menuOpen()) setMenu(false);
  };
  el.addEventListener('click', onNav);

  return () => {
    ro.disconnect();
    burger?.removeEventListener('click', onBurger);
    document.removeEventListener('keydown', onEsc);
    phoneNav.removeEventListener('change', syncInert);
    el.removeEventListener('click', onNav);
    setMenu(false);
  };
}

/* ---------------------------------------------------------
   HERO — character select
   --------------------------------------------------------- */
function initHero(el, ctx) {
  const castEl = $('[data-dr-cast]', el);
  if (!castEl) return;
  let cast = [];
  let copy = {};
  try { cast = JSON.parse(castEl.textContent); } catch (e) { cast = []; }
  try { copy = JSON.parse($('[data-dr-copy]', el)?.textContent || '{}'); } catch (e) { copy = {}; }
  if (!cast.length) return;

  const nameEl = $('[data-dr-name]', el), kickerEl = $('[data-dr-kicker]', el), tagEl = $('[data-dr-tag]', el), weightEl = $('[data-dr-weight]', el);
  const charWrap = $('[data-dr-char]', el), charImg = $('[data-dr-char-img]', el), shadow = $('.dr-hero__shadow', el), cta = $('[data-dr-cta]', el);
  const panel = $('[role="tabpanel"]', el);
  const wipe = $('.dr-hero__wipe', el), wipeSvg = $('.dr-hero__wipe-drips', el), wipePath = $('path', wipeSvg);
  const tabs = $$('.dr-tab', el);
  const interval = Number(el.dataset.interval) || 6;
  el.style.setProperty('--dr-interval', `${interval}s`);
  let current = 0, shown = 0, pending = -1, busy = false;
  let auto = el.dataset.autoplay === 'true' && !reduce && cast.length > 1;

  cast.forEach((c) => { const i = new Image(); i.src = c.img; });
  // the floor shadow follows each character's real proportions (the box itself is fixed)
  const setAr = () => { if (charImg.naturalWidth) charWrap.style.setProperty('--char-ar', (charImg.naturalWidth / charImg.naturalHeight).toFixed(3)); };
  charImg.addEventListener('load', setAr);
  setAr();
  const tinted = [el, $('.dr-header')].filter(Boolean); // the only places that use the character colours
  gsap.set(tinted, { '--dr-bg': cast[0].bg, '--dr-deep': cast[0].deep, '--dr-kick': cast[0].kick });

  function setName(text) {
    const chars = [...(text || '')];
    const n = chars.length;
    nameEl.setAttribute('aria-label', text);
    nameEl.innerHTML = chars.map((ch, i) => {
      const t = Math.sin(Math.PI * ((i + 0.5) / n));
      return `<span class="ch" aria-hidden="true" style="--s:${(0.84 + 0.16 * t).toFixed(3)};--y:${(-0.05 * t).toFixed(3)}em"><span class="ci">${ch === ' ' ? '&nbsp;' : ch.replace(/[<>&]/g, '')}</span></span>`;
    }).join('');
    return $$('.ci', nameEl);
  }

  function ctaText(c) {
    if (!c.variant) return `${copy.shop || 'Shop'} ${c.name}`;
    if (!c.available) return copy.soldOut || 'Sold out';
    return `${copy.prefix || 'Grab'} ${c.name}`;
  }

  // ARIA tabs: only the selected tab is in the Tab order; arrows, Home and End move between them
  const markTab = (i) => tabs.forEach((t, k) => {
    t.setAttribute('aria-selected', k === i);
    t.tabIndex = k === i ? 0 : -1;
    if (k === i && panel && t.id) panel.setAttribute('aria-labelledby', t.id);
  });

  function applyText(c, i) {
    shown = i;
    kickerEl.textContent = c.kicker;
    tagEl.textContent = c.tag;
    weightEl.textContent = c.weight;
    charImg.src = c.img;
    charImg.alt = c.alt || c.name;
    $('.dr-btn__label', cta).textContent = ctaText(c);
    $('.dr-btn__price', cta).textContent = c.available ? c.price : '';
    cta.setAttribute('aria-disabled', String(Boolean(c.variant) && !c.available)); // sold out: nothing to grab
    $('meta[name="theme-color"]')?.setAttribute('content', c.bg);
  }

  let wipeDrips = [], wipeH = 0;
  function layoutWipe() {
    const w = el.offsetWidth;
    wipeDrips = makeDrips(w, { seed: 5, gap: [40, 110], r: [10, 22], len: [30, 120] });
    wipeH = 1 + Math.max(...wipeDrips.map((d) => d.len)) * 2.6 + 30;
    sizeSvg(wipeSvg, w, wipeH);
    wipePath.setAttribute('d', dripPath(w, wipeDrips, 1, 1));
    gsap.set(wipe, { y: -(el.offsetHeight + wipeH) });
  }
  layoutWipe();
  el._drLayout = layoutWipe;

  const lettersIn = (letters, delay = 0, { duration = 0.9, stagger = 0.055 } = {}) => gsap.fromTo(letters,
    { yPercent: -150, rotation: () => gsap.utils.random(-30, 30), opacity: 0 },
    { yPercent: 0, rotation: 0, opacity: 1, duration, ease: 'back.out(2.4)', stagger, delay });

  // Autoplay: the active tab's bar fills in CSS (no per-frame JS); when it ends, the next character pours in.
  // It holds while the visitor has scrolled on (40% of the hero gone) or the tab is in the background.
  const holds = new Set();
  const hold = (why, on) => { if (on) holds.add(why); else holds.delete(why); el.classList.toggle('is-held', holds.size > 0); };
  function runCycle() {
    tabs.forEach((t) => t.classList.remove('is-timing'));
    if (!auto) return;
    const t = tabs[current];
    void t.offsetWidth; // restart the fill, even on the same tab
    t.classList.add('is-timing');
  }
  const onBarEnd = (e) => {
    if (e.animationName !== 'dr-fill' || !auto || busy) return;
    select((current + 1) % cast.length);
  };
  // the CTA's wiggle restarts after every switch, so it lands a beat after the pour instead of mid-pour
  const restartNudge = () => {
    if (reduce || !el.classList.contains('dr-ready')) return;
    cta.style.animation = 'none';
    void cta.offsetWidth;
    cta.style.animation = '';
  };
  const pauseBtn = $('[data-dr-pause]', el);
  const syncPause = () => {
    if (!pauseBtn) return;
    pauseBtn.hidden = reduce || cast.length < 2;
    pauseBtn.setAttribute('aria-pressed', String(!auto));
    pauseBtn.setAttribute('aria-label', auto ? 'Pause the character slideshow' : 'Play the character slideshow');
    pauseBtn.dataset.state = auto ? 'playing' : 'paused';
  };
  function stopAuto() {
    auto = false;
    syncPause();
    nameEl.setAttribute('aria-live', 'polite'); // announce names only once the visitor drives the switcher
    tabs.forEach((t) => t.classList.remove('is-timing'));
  }

  const finish = () => {
    busy = false;
    const next = pending;
    pending = -1;
    if (next > -1 && next !== current) select(next);
    else { markTab(current); restartNudge(); if (auto) runCycle(); }
  };

  const select = ctx.add('select', (i) => {
    if (!cast[i]) return;
    if (busy) { pending = i; markTab(i); return; } // run it as soon as the current pour lands
    if (i === current) return;
    busy = true;
    pending = -1;
    markTab(i);
    const c = cast[i];
    current = i;
    if (reduce) {
      // reduced motion: a short crossfade instead of the pour
      const fade = [nameEl, kickerEl, tagEl, weightEl, charWrap];
      gsap.timeline({ onComplete: finish })
        .to(fade, { opacity: 0, duration: 0.1, ease: 'none' })
        .add(() => {
          gsap.set(tinted, { '--dr-bg': c.bg, '--dr-deep': c.deep, '--dr-kick': c.kick });
          applyText(c, i);
          setName(c.name);
        })
        .to(fade, { opacity: 1, duration: 0.1, ease: 'none' });
      return;
    }
    const H = el.offsetHeight;
    wipe.style.setProperty('--wipe', c.bg);
    const stretch = { v: 1 };
    gsap.timeline({ onComplete: finish })
      .set(wipe, { y: -(H + wipeH) })
      .to(wipe, { y: 0, duration: 0.9, ease: 'power3.inOut' }, 0)
      .to(stretch, { v: 2.6, duration: 0.45, ease: 'power2.in', yoyo: true, repeat: 1,
        onUpdate: () => wipePath.setAttribute('d', dripPath(el.offsetWidth, wipeDrips, 1, stretch.v)) }, 0)
      .to(tinted, { '--dr-deep': c.deep, '--dr-kick': c.kick, duration: 0.9, ease: 'power2.inOut' }, 0)
      .to($$('.ci', nameEl), { yPercent: 140, rotation: () => gsap.utils.random(-35, 35), opacity: 0, duration: 0.42, ease: 'power2.in', stagger: 0.035 }, 0)
      .to(charImg, { y: 90, rotation: -10, scale: 0.86, opacity: 0, duration: 0.4, ease: 'power2.in' }, 0.05)
      .to(shadow, { scale: 0.6, opacity: 0, duration: 0.4, ease: 'power2.in' }, 0.05)
      .to([kickerEl, tagEl, weightEl], { y: -14, opacity: 0, duration: 0.28, ease: 'power2.in', stagger: 0.04 }, 0)
      .add(() => {
        gsap.set(tinted, { '--dr-bg': c.bg });
        gsap.set(wipe, { y: -(H + wipeH) });
        applyText(c, i);
        lettersIn(setName(c.name));
      }, 0.9)
      .fromTo(charImg, { y: 120, rotation: 8, scale: 0.8, opacity: 0 }, { y: 0, rotation: 0, scale: 1, opacity: 1, duration: 0.9, ease: 'back.out(1.7)', immediateRender: false }, 1.0)
      .fromTo(shadow, { scale: 0.6, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.4, ease: 'power2.out', immediateRender: false }, 1.0)
      .fromTo([kickerEl, tagEl, weightEl], { y: 16, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out', stagger: 0.07, immediateRender: false }, 1.05);
  });

  el._drSelect = (i) => { stopAuto(); select(i); };
  el._drSelectBlock = (blockId) => {
    const i = cast.findIndex((c) => c.id === blockId);
    if (i > -1) el._drSelect(i);
  };

  const onTab = (e) => {
    const t = e.target.closest('.dr-tab');
    if (!t) return;
    stopAuto();
    select(Number(t.dataset.index));
  };
  const onKey = (e) => {
    const n = cast.length, from = pending > -1 ? pending : current; // move from the latest pick, even mid-switch
    const next = { ArrowRight: (from + 1) % n, ArrowLeft: (from - 1 + n) % n, Home: 0, End: n - 1 }[e.key];
    if (next === undefined) return;
    e.preventDefault();
    stopAuto(); select(next); tabs[next]?.focus();
  };
  const tabsWrap = $('.dr-hero__tabs', el);
  tabsWrap?.addEventListener('click', onTab);
  tabsWrap?.addEventListener('keydown', onKey);
  tabsWrap?.addEventListener('animationend', onBarEnd);
  // WCAG 2.2.2: a visible pause/play for the autoplay, and keyboard focus on the controls stops it
  const onPause = () => {
    if (auto) { stopAuto(); return; }
    auto = true;
    nameEl.setAttribute('aria-live', 'off');
    syncPause();
    if (!busy) runCycle();
  };
  const onFocusIn = (e) => { if (auto && e.target.closest('.dr-tab, .dr-hero__cta')) stopAuto(); };
  pauseBtn?.addEventListener('click', onPause);
  el.addEventListener('focusin', onFocusIn);
  syncPause();

  const onCta = async () => {
    if (cta.getAttribute('aria-busy') === 'true') return; // one add at a time: double taps add one jar
    stopAuto();
    const c = cast[shown]; // the character the label names, even mid-switch
    if (!c.variant) { window.location.assign(c.url); return; }
    if (!c.available) return;
    cta.setAttribute('aria-busy', 'true');
    try { await addToCart(c.variant, charImg, c.img, 'hero'); } finally { cta.removeAttribute('aria-busy'); }
  };
  cta?.addEventListener('click', onCta);

  let sx = 0, sy = 0;
  const onTouchStart = (e) => { sx = e.touches[0].clientX; sy = e.touches[0].clientY; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - sx, dy = e.changedTouches[0].clientY - sy;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.4) {
      stopAuto();
      const from = pending > -1 ? pending : current;
      select((from + (dx < 0 ? 1 : -1) + cast.length) % cast.length);
    }
  };
  el.addEventListener('touchstart', onTouchStart, { passive: true });
  el.addEventListener('touchend', onTouchEnd, { passive: true });

  let onMove = null, onLeave = null;
  if (finePointer && !reduce) {
    const qx = gsap.quickTo(charWrap, 'x', { duration: 0.9, ease: 'power3' });
    const qr = gsap.quickTo(charWrap, 'rotation', { duration: 0.9, ease: 'power3' });
    const nx = gsap.quickTo(nameEl, 'x', { duration: 1.1, ease: 'power3' });
    const ny = gsap.quickTo(nameEl, 'y', { duration: 1.1, ease: 'power3' });
    onMove = (e) => {
      const px = e.clientX / innerWidth - 0.5, py = e.clientY / innerHeight - 0.5;
      qx(px * 46); qr(px * 5); nx(px * -22); ny(py * -12);
    };
    onLeave = () => { qx(0); qr(0); nx(0); ny(0); };
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', onLeave);
  }
  // the idle bob is CSS (.dr-hero__float)

  const onVisibility = () => hold('tab', document.hidden);
  document.addEventListener('visibilitychange', onVisibility);
  ScrollTrigger.create({ trigger: el, start: 'top top', end: '40% top', onLeave: () => hold('scroll', true), onEnterBack: () => hold('scroll', false) });

  el._drIntro = ctx.add('intro', ({ instant = false, light = false } = {}) => {
    const letters = setName(cast[0].name);
    if (reduce) return;
    if (instant) { runCycle(); el.classList.add('dr-ready'); return; }
    if (light) {
      // repeat visits: only the name drops in, quickly
      gsap.timeline()
        .add(lettersIn(letters, 0, { duration: 0.5, stagger: 0.03 }), 0)
        .add(runCycle, 0.6)
        .add(() => el.classList.add('dr-ready'), 0.7);
      return;
    }
    gsap.timeline()
      .add(lettersIn(letters), 0)
      .from(charImg, { y: 140, scale: 0.8, rotation: -8, opacity: 0, duration: 1.1, ease: 'back.out(1.6)' }, 0.15)
      .from(shadow, { scale: 0.6, opacity: 0, duration: 0.6, ease: 'power2.out' }, 0.35)
      .from([kickerEl, tagEl, weightEl], { y: 18, opacity: 0, duration: 0.6, ease: 'power3.out', stagger: 0.08 }, 0.3)
      .from([...tabs, cta], { y: 30, opacity: 0, duration: 0.6, ease: 'power3.out', stagger: 0.06, clearProps: 'transform,opacity' }, 0.5)
      .add(runCycle, 1.2)
      .add(() => el.classList.add('dr-ready'), 1.3); // CTA nudge (CSS) starts only once GSAP has let go of its transform
  });
  setName(cast[0].name);
  applyText(cast[0], 0);
  markTab(0);

  return () => {
    stopAuto();
    tabsWrap?.removeEventListener('click', onTab);
    tabsWrap?.removeEventListener('keydown', onKey);
    tabsWrap?.removeEventListener('animationend', onBarEnd);
    pauseBtn?.removeEventListener('click', onPause);
    el.removeEventListener('focusin', onFocusIn);
    cta?.removeEventListener('click', onCta);
    el.removeEventListener('touchstart', onTouchStart);
    el.removeEventListener('touchend', onTouchEnd);
    if (onMove) { el.removeEventListener('pointermove', onMove); el.removeEventListener('pointerleave', onLeave); }
    document.removeEventListener('visibilitychange', onVisibility);
    el.classList.remove('is-held');
  };
}

/* ---------------------------------------------------------
   VELOCITY BAND
   --------------------------------------------------------- */
function initBand(el) {
  if (reduce) return; // reduced motion: CSS keeps the words still
  // The words loop in CSS (off the main thread). Scrolling only changes that animation's speed and direction.
  const anims = () => $$('[data-marquee]', el).flatMap((t) => t.getAnimations?.() || []);
  // start deep into the loop, so running it backwards (scrolling up) never hits its start and stops
  anims().forEach((a) => { if ((a.currentTime || 0) < 1e6) a.currentTime = 1e7; });
  const row = $('.dr-band__row', el);
  const skew = gsap.quickTo(row, 'skewX', { duration: 0.5, ease: 'power3' });
  const speed = { r: 1 };
  const apply = () => anims().forEach((a) => { a.playbackRate = speed.r; });
  ScrollTrigger.create({
    trigger: el, start: 'top bottom', end: 'bottom top',
    onUpdate: (self) => {
      const v = self.getVelocity();
      const dir = self.direction;
      skew(gsap.utils.clamp(-8, 8, v / -220));
      gsap.to(speed, { r: dir * (1 + Math.min(Math.abs(v) / 260, 6)), duration: 0.2, overwrite: true, onUpdate: apply,
        onComplete: () => gsap.to(speed, { r: dir, duration: 0.9, ease: 'power2.out', overwrite: true, onUpdate: apply }) });
    },
  });
  const settle = () => skew(0);
  ScrollTrigger.addEventListener('scrollEnd', settle);
  return () => { ScrollTrigger.removeEventListener('scrollEnd', settle); gsap.killTweensOf(speed); speed.r = 1; apply(); };
}

/* ---------------------------------------------------------
   THE JAR — pinned, scrubbed story
   --------------------------------------------------------- */
function initJar(el) {
  const titleL = $('.dr-jar__title--l', el), titleR = $('.dr-jar__title--r', el);
  const img = $('.dr-jar__img', el), sun = $('.dr-jar__sun', el), stage = $('.dr-jar__stage', el);
  const charsL = titleL ? splitChars(titleL) : [];
  const charsR = titleR ? splitChars(titleR) : [];
  const mm = gsap.matchMedia();

  mm.add('(min-width: 961px) and (prefers-reduced-motion: no-preference)', () => {
    const notes = $$('.dr-note', el);
    $$('.dr-note__arrow path', el).forEach((p) => { const l = p.getTotalLength(); gsap.set(p, { strokeDasharray: l, strokeDashoffset: l }); });
    // No JS pinning: the stage is CSS-sticky inside a tall section, so the wheel always scrolls
    // Horizon's .page-wrapper (a fixed-position pin would swallow wheel events there).
    const tl = gsap.timeline({ scrollTrigger: { trigger: el, start: 'top top', end: 'bottom bottom', scrub: 1 } });
    tl.from(sun, { scale: 0.15, rotation: -120, duration: 1, ease: 'power2.out' }, 0)
      .from(img, { yPercent: 55, rotation: -16, scale: 0.62, duration: 1, ease: 'power2.out' }, 0)
      .from(charsL, { yPercent: 120, rotation: () => gsap.utils.random(-20, 20), opacity: 0, stagger: 0.03, duration: 0.5, ease: 'back.out(2)' }, 0.25);
    notes.forEach((n, i) => {
      tl.from($('.dr-note__txt', n), { scale: 0.5, rotation: gsap.utils.random(-18, 18), opacity: 0, duration: 0.3, ease: 'back.out(2.5)' }, 0.8 + i * 0.28);
      const path = $('.dr-note__arrow path', n);
      if (path) tl.to(path, { strokeDashoffset: 0, duration: 0.3, ease: 'power2.out' }, 0.9 + i * 0.28);
    });
    tl.from(charsR, { yPercent: 120, rotation: () => gsap.utils.random(-20, 20), opacity: 0, stagger: 0.03, duration: 0.5, ease: 'back.out(2)' }, 1.9)
      .to(img, { rotation: 5, duration: 0.8, ease: 'sine.inOut' }, 1.8)
      .to(sun, { rotation: 40, duration: 2.6, ease: 'none' }, 0);
  });

  mm.add('(max-width: 960px) and (prefers-reduced-motion: no-preference)', () => {
    const center = $('.dr-jar__center', el) || stage;
    gsap.from(img, { yPercent: 25, rotation: -10, scale: 0.8, opacity: 0, duration: 1.1, ease: 'back.out(1.4)', scrollTrigger: { trigger: center, start: 'center 75%' } });
    gsap.from(sun, { scale: 0.3, duration: 1.2, ease: 'power3.out', scrollTrigger: { trigger: center, start: 'center 75%' } });
    [[titleL, charsL], [titleR, charsR]].forEach(([t, chars]) => t && gsap.from(chars, { yPercent: 110, opacity: 0, stagger: 0.03, duration: 0.6, ease: 'back.out(2)', scrollTrigger: { trigger: t, start: 'top 85%' } }));
  });

  return () => mm.revert();
}

/* ---------------------------------------------------------
   HEADINGS — letters bounce in once
   --------------------------------------------------------- */
function bounceHeadings(el) {
  if (reduce) return;
  $$('[data-split]', el).forEach((h) => {
    gsap.from(splitChars(h), { yPercent: 80, rotation: () => gsap.utils.random(-25, 25), opacity: 0, duration: 0.8, ease: 'back.out(2.2)', stagger: 0.035,
      scrollTrigger: { trigger: h, start: 'top 85%', once: true } });
  });
}

/* ---------------------------------------------------------
   CARDS — tilt, holo foil, flip, add to cart
   --------------------------------------------------------- */
function initCards(el) {
  bounceHeadings(el);
  const cards = $$('.dr-card', el);
  if (!reduce && cards.length) {
    gsap.from(cards, { y: 160, rotation: (i) => (i - (cards.length - 1) / 2) * 10, opacity: 0, duration: 1, ease: 'back.out(1.3)', stagger: 0.14,
      scrollTrigger: { trigger: $('.dr-cards', el), start: 'top 80%', once: true } });
  }
  const offs = [];
  cards.forEach((card) => {
    const inner = $('.dr-card__inner', card), back = $('.dr-card__back', card), front = $('.dr-card__front', card);
    const glare = $('.dr-card__glare', card), foil = $('.dr-card__foil', card);
    const flipButtons = $$('.dr-card__flip', card);
    let flipped = false;

    if (finePointer && !reduce) {
      const rx = gsap.quickTo(card, 'rotationX', { duration: 0.6, ease: 'power3' });
      const ry = gsap.quickTo(card, 'rotationY', { duration: 0.6, ease: 'power3' });
      const move = (e) => {
        const r = card.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height;
        rx((0.5 - py) * 16); ry((px - 0.5) * 20);
        glare?.style.setProperty('--gx', `${px * 100}%`); glare?.style.setProperty('--gy', `${py * 100}%`);
        foil?.style.setProperty('--fx', `${px * 100}%`); foil?.style.setProperty('--fy', `${py * 100}%`);
      };
      const enter = () => card.classList.add('is-hover');
      const leave = () => { rx(0); ry(0); card.classList.remove('is-hover'); };
      card.addEventListener('pointermove', move);
      card.addEventListener('pointerenter', enter);
      card.addEventListener('pointerleave', leave);
      offs.push(() => { card.removeEventListener('pointermove', move); card.removeEventListener('pointerenter', enter); card.removeEventListener('pointerleave', leave); });
    }

    const bars = $$('.dr-stats i', card);
    const flip = () => {
      flipped = !flipped;
      flipButtons.forEach((b) => b.setAttribute('aria-pressed', flipped));
      const showing = flipped ? back : front, hiding = flipped ? front : back;
      const hadFocus = card.contains(document.activeElement);
      showing.inert = false;
      // focus moves to the face that's turning in before the other one goes inert, so it never drops to <body>
      if (hadFocus) $('.dr-card__flip', showing)?.focus({ preventScroll: true });
      hiding.inert = true;
      gsap.killTweensOf(inner);
      if (reduce) {
        // reduced motion: a quick fade instead of the 3D turn
        gsap.timeline()
          .to(inner, { opacity: 0, duration: 0.1, ease: 'none' })
          .add(() => {
            gsap.set(inner, { rotationY: flipped ? 180 : 0 });
            gsap.set(bars, { scaleX: (i, b) => (flipped ? Number(b.dataset.v) : 0) });
          })
          .to(inner, { opacity: 1, duration: 0.1, ease: 'none' });
      } else {
        gsap.timeline()
          .to(inner, { rotationY: flipped ? 180 : 0, duration: 0.75, ease: 'power3.inOut' }, 0)
          .to(inner, { z: 60, duration: 0.375, ease: 'power2.out' }, 0)
          .to(inner, { z: 0, duration: 0.375, ease: 'power2.in' }, 0.375);
        if (flipped) gsap.fromTo(bars, { scaleX: 0 }, { scaleX: (i, b) => Number(b.dataset.v), duration: 0.8, ease: 'power3.out', stagger: 0.08, delay: 0.4 });
        else gsap.to(bars, { scaleX: 0, duration: 0.2 });
      }
    };
    // Tap anywhere on the card flips it (except the buy button and links); the ↻ button is the keyboard route.
    const onClick = (e) => { if (!e.target.closest('.dr-card__add, a')) flip(); };
    inner.addEventListener('click', onClick);

    const add = $('.dr-card__front .dr-card__add', card);
    const onAdd = async (e) => {
      e.stopPropagation();
      if (!add.dataset.variant || add.getAttribute('aria-busy') === 'true') return;
      add.setAttribute('aria-busy', 'true');
      try { await addToCart(add.dataset.variant, $('.dr-card__art img', card), add.dataset.img, 'cards'); } finally { add.removeAttribute('aria-busy'); }
    };
    add?.addEventListener('click', onAdd);
    offs.push(() => { inner.removeEventListener('click', onClick); add?.removeEventListener('click', onAdd); });
  });

  // "Get all 3": one request, one jar of each
  const bundle = $('[data-dr-bundle]', el);
  const onBundle = async () => {
    if (bundle.getAttribute('aria-busy') === 'true') return;
    bundle.setAttribute('aria-busy', 'true');
    const jars = cards.map((card) => [$('.dr-card__art img', card), $('.dr-card__front .dr-card__add', card)?.dataset.img]).filter(([img, src]) => img && src);
    try { await addToCart(bundle.dataset.drBundle.split(','), jars.map((j) => j[0]), jars.map((j) => j[1]), 'bundle'); } finally { bundle.removeAttribute('aria-busy'); }
  };
  bundle?.addEventListener('click', onBundle);
  offs.push(() => bundle?.removeEventListener('click', onBundle));
  return () => offs.forEach((off) => off());
}

/* ---------------------------------------------------------
   OPEN WIDE — the frame opens up as you scroll
   --------------------------------------------------------- */
function initWide(el) {
  const frame = $('.dr-wide__frame', el), img = $('.dr-wide__frame img', el), title = $('.dr-wide__title', el);
  const mm = gsap.matchMedia();
  mm.add('(prefers-reduced-motion: no-preference)', () => {
    const small = innerWidth < 961;
    const chars = title ? new SplitText($$('span', title), { type: 'chars', charsClass: 'ch' }).chars : [];
    const tl = gsap.timeline({ scrollTrigger: { trigger: el, start: 'top top', end: 'bottom bottom', scrub: 1 } });
    tl.fromTo(frame, { clipPath: small ? 'inset(20% 8% 26% 8% round 28px)' : 'inset(24% 32% 24% 32% round 36px)' },
      { clipPath: 'inset(0% 0% 0% 0% round 0px)', duration: 1, ease: 'none' }, 0);
    if (img) tl.fromTo(img, { scale: 1.4 }, { scale: 1, duration: 1, ease: 'none' }, 0);
    if (chars.length) tl.from(chars, { yPercent: 130, rotation: () => gsap.utils.random(-25, 25), opacity: 0, duration: 0.25, stagger: 0.035, ease: 'back.out(2)' }, 0.62);
  });
  mm.add('(prefers-reduced-motion: reduce)', () => gsap.set(frame, { clipPath: 'none' }));
  return () => mm.revert();
}

/* ---------------------------------------------------------
   IN THE WILD — photos land on the board, drag them around
   --------------------------------------------------------- */
/* Draggable + Inertia are only needed for dragging polaroids on desktop, so they load there, on demand */
const scripts = {};
const loadScript = (src) => (scripts[src] ||= new Promise((resolve, reject) => {
  const tag = document.createElement('script');
  tag.src = src;
  tag.onload = resolve;
  tag.onerror = reject;
  document.head.appendChild(tag);
}));
async function loadDrag(el) {
  await Promise.all([
    window.Draggable || loadScript(el.dataset.dragJs),
    window.InertiaPlugin || loadScript(el.dataset.inertiaJs),
  ]);
  gsap.registerPlugin(window.Draggable, window.InertiaPlugin);
}

function initWild(el) {
  bounceHeadings(el);
  const snaps = $$('.dr-snap', el);
  snaps.forEach((s) => { s._r = parseFloat(s.style.getPropertyValue('--r')) || 0; gsap.set(s, { rotation: s._r }); });
  if (!reduce && snaps.length) {
    gsap.from(snaps, { y: 260, rotation: () => gsap.utils.random(-40, 40), opacity: 0, duration: 1.1, ease: 'back.out(1.3)', stagger: 0.12,
      scrollTrigger: { trigger: $('.dr-wild__board', el), start: 'top 78%', once: true } });
  }
  let z = 10;
  const toFront = (s, bounce) => {
    s.style.zIndex = ++z;
    if (bounce && !reduce) gsap.fromTo(s, { scale: 1.08 }, { scale: 1, duration: 0.5, ease: 'back.out(2)' });
  };
  // keyboard: a focused polaroid comes to the front (on phones they're fanned out and overlap); Enter/Space too
  const onFocus = (e) => toFront(e.currentTarget, false);
  const onKey = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toFront(e.currentTarget, true); } };
  snaps.forEach((s) => { s.addEventListener('focus', onFocus); s.addEventListener('keydown', onKey); });

  const mm = gsap.matchMedia();
  mm.add('(min-width: 961px)', () => {
    if (designMode || !el.dataset.dragJs) return;
    let ds = [], gone = false;
    loadDrag(el).then(() => {
      if (gone || !window.Draggable) return;
      ds = window.Draggable.create(snaps, {
        type: 'x,y', bounds: $('.dr-wild__board', el), inertia: true, edgeResistance: 0.7,
        onPress() { toFront(this.target, false); gsap.to(this.target, { scale: 1.06, duration: 0.2, ease: 'power3.out' }); },
        onDrag() { gsap.to(this.target, { rotation: this.target._r + gsap.utils.clamp(-14, 14, this.deltaX * 0.9), duration: 0.35, ease: 'power3.out', overwrite: 'auto' }); },
        onRelease() { gsap.to(this.target, { scale: 1, rotation: this.target._r, duration: 0.5, ease: 'back.out(2)' }); },
      });
    }).catch(() => { /* no dragging, the photos still show */ });
    return () => { gone = true; ds.forEach((d) => d.kill()); };
  });
  // phones/tablets: the polaroids are fanned out and overlap, so a tap brings one to the front
  mm.add('(max-width: 960px)', () => {
    const onTap = (e) => toFront(e.currentTarget, true);
    snaps.forEach((s) => s.addEventListener('click', onTap));
    return () => snaps.forEach((s) => { s.removeEventListener('click', onTap); s.style.zIndex = ''; });
  });
  return () => {
    mm.revert();
    snaps.forEach((s) => { s.removeEventListener('focus', onFocus); s.removeEventListener('keydown', onKey); });
  };
}

/* ---------------------------------------------------------
   PICKLE CLUB — the cast peeks over the card
   --------------------------------------------------------- */
function initClub(el) {
  bounceHeadings(el);
  const peeks = $$('.dr-peek', el);
  if (!reduce && peeks.length) {
    gsap.from(peeks, { yPercent: 100, duration: 0.9, ease: 'back.out(1.8)', stagger: 0.12, scrollTrigger: { trigger: $('.dr-club__wrap', el), start: 'top 80%', once: true } });
    // their idle sway is CSS on the img inside each peek (drippy.css: dr-sway)
  }
  const email = $('input[type="email"]', el);
  const up = () => !reduce && gsap.to(peeks, { y: -24, duration: 0.4, ease: 'back.out(2.5)', stagger: 0.05 });
  const down = () => !reduce && gsap.to(peeks, { y: 0, duration: 0.4, ease: 'power3.out' });
  email?.addEventListener('focus', up);
  email?.addEventListener('blur', down);
  if ($('.dr-club__msg--success', el) && !reduce) {
    gsap.fromTo(peeks, { y: 0 }, { y: -60, duration: 0.3, ease: 'power2.out', yoyo: true, repeat: 3, stagger: 0.08, delay: 0.6 });
  }
  return () => { email?.removeEventListener('focus', up); email?.removeEventListener('blur', down); };
}

/* ---------------------------------------------------------
   FOOTER — gooey dripping wordmark
   --------------------------------------------------------- */
function initFooter(el) {
  const svg = $('.dr-footer__mark', el);
  const group = svg && $('[data-goo]', svg);
  const text = svg && $('.dr-footer__word', svg);
  if (!svg || !group || !text || reduce) return;
  const NS = 'http://www.w3.org/2000/svg';
  let spots = [];
  try {
    const n = text.getNumberOfChars();
    for (let i = 0; i < n; i++) {
      const b = text.getExtentOfChar(i), ch = text.textContent[i];
      if (ch === ' ') continue;
      const desc = 'pyqgj'.includes(ch);
      spots.push({ x0: b.x + b.width * 0.22, x1: b.x + b.width * (desc ? 0.42 : 0.78), y: desc ? b.y + b.height - 22 : 292 });
    }
  } catch (e) { spots = [{ x0: 200, x1: 1000, y: 292 }]; }
  if (!spots.length) return;

  // The goo filter repaints the whole wordmark while any drop moves, so only a few drip at once.
  let extra = 0;
  const drop = (x, y, delay = 0, repeating = true) => {
    if (!repeating && extra >= 2) return;
    if (!repeating) extra++;
    const c = document.createElementNS(NS, 'circle');
    group.appendChild(c);
    const tl = gsap.timeline({ delay, repeat: repeating ? -1 : 0, repeatDelay: gsap.utils.random(0.8, 2.8), onComplete: () => { if (!repeating) { extra--; c.remove(); } } });
    if (repeating) loop(el, tl);
    tl
      .set(c, { attr: { cx: x, cy: y - 8, r: 0 } })
      .to(c, { attr: { r: gsap.utils.random(11, 19), cy: y + 6 }, duration: gsap.utils.random(0.9, 1.6), ease: 'sine.inOut' })
      .to(c, { attr: { cy: y + 190, r: 5 }, duration: 0.75, ease: 'power2.in' })
      .to(c, { attr: { r: 0 }, duration: 0.1 });
  };
  const loopers = spots.filter((s, i) => i % 2 === 1).slice(0, 3); // r, p, y in "drippy"
  (loopers.length ? loopers : spots.slice(0, 3)).forEach((s, i) => drop(gsap.utils.random(s.x0, s.x1), s.y, i * 0.7));

  let last = 0;
  const onMove = (e) => {
    const now = performance.now();
    if (now - last < 110) return;
    last = now;
    const pt = new DOMPoint(e.clientX, e.clientY).matrixTransform(svg.getScreenCTM().inverse());
    const s = spots.find((sp) => pt.x >= sp.x0 - 30 && pt.x <= sp.x1 + 30);
    if (s) drop(gsap.utils.clamp(s.x0, s.x1, pt.x), s.y, 0, false);
  };
  svg.addEventListener('pointermove', onMove);
  return () => { svg.removeEventListener('pointermove', onMove); $$('circle', group).forEach((c) => c.remove()); };
}

/* ---------------------------------------------------------
   SECTION LIFECYCLE
   --------------------------------------------------------- */
const INITS = { header: initHeader, hero: initHero, band: initBand, jar: initJar, cards: initCards, wide: initWide, wild: initWild, club: initClub, footer: initFooter };

function mount(scope = document, { edges = true } = {}) {
  const roots = scope.matches?.('[data-drippy]') ? [scope] : $$('[data-drippy]', scope);
  roots.forEach((el) => {
    if (el._drCtx) return;
    const fn = INITS[el.dataset.drippy];
    if (!fn) return;
    let cleanup;
    const ctx = gsap.context((self) => { cleanup = fn(el, self); }, el);
    el._drCtx = ctx;
    el._drCleanup = cleanup;
    el._drIO = watchVisibility(el);
  });
  if (edges) buildEdges(scope.matches?.('[data-drippy]') ? scope.parentElement || document : scope);
}

function unmount(scope) {
  const roots = scope.matches?.('[data-drippy]') ? [scope] : $$('[data-drippy]', scope);
  roots.forEach((el) => {
    try { el._drCleanup?.(); } catch (e) { /* keep going */ }
    el._drIO?.disconnect();
    el._drLoops = [];
    el._drCtx?.revert();
    el._drCtx = null;
    el._drCleanup = null;
  });
}

const intro = (scope = document, { light = false } = {}) => {
  const instant = root.classList.contains('dr-static');
  $$('[data-drippy="hero"]', scope).forEach((h) => h._drIntro?.({ instant, light }));
  root.classList.remove('dr-pre');
};

/* Arriving on /#shop (from another page, the 404 or the empty cart): once everything below the fold is
   set up, land exactly on it — unless the visitor has already started scrolling. */
let touched = false;
['wheel', 'touchstart', 'keydown'].forEach((type) => addEventListener(type, () => { touched = true; }, { once: true, passive: true }));
const landOnHash = () => {
  if (touched || location.hash.length < 2) return;
  try { if (document.getElementById(decodeURIComponent(location.hash.slice(1)))) scrollToHash(location.hash, { instant: true }); } catch (e) { /* not an id */ }
};

document.addEventListener('shopify:section:load', (e) => {
  mount(e.target);
  intro(e.target);
  ScrollTrigger.refresh();
});
document.addEventListener('shopify:section:unload', (e) => { unmount(e.target); });
document.addEventListener('shopify:section:reorder', () => ScrollTrigger.refresh());
document.addEventListener('shopify:block:select', (e) => {
  const hero = e.target.closest?.('[data-drippy="hero"]');
  if (hero?._drSelectBlock) hero._drSelectBlock(e.detail?.blockId);
});

let resizeTimer, lastW = innerWidth;
addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    if (innerWidth === lastW) return; // toolbar show/hide only changes the height
    lastW = innerWidth;
    buildEdges();
    $$('[data-drippy="hero"]').forEach((h) => h._drLayout?.());
  }, 200);
});
addEventListener('load', () => ScrollTrigger.refresh());

/* ---------------------------------------------------------
   BOOT — preloader (first visit only) → sections → hero intro
   --------------------------------------------------------- */
function useScroller() {
  ScrollTrigger.defaults({ scroller: scrollEl() });
}

squeeze.addEventListener('change', () => {
  unmount(document);
  useScroller();
  mount(document);
  ScrollTrigger.refresh();
});

async function boot() {
  useScroller();
  initCursor();
  const loader = $('[data-dr-loader]');
  // Only the hero's display face gates the first paint; the other fonts swap in on their own.
  const displayFont = document.fonts?.load ? document.fonts.load('900 1em Fraunces').catch(() => {}) : Promise.resolve();
  const heroEl = $('[data-drippy="hero"]'), headerEl = $('[data-drippy="header"]');
  const firstMount = () => { [headerEl, heroEl].forEach((el) => el && mount(el, { edges: false })); };
  // Everything below the fold is set up once the intro is running and the browser is idle.
  const restMount = () => idle(() => { mount(); ScrollTrigger.refresh(); landOnHash(); });

  if (!loader || reduce) {
    dropLoader();
    await Promise.race([displayFont, wait(800)]);
    firstMount();
    intro(document, { light: root.classList.contains('dr-seen') }); // repeat visits get the short intro
    restMount();
    return;
  }

  const count = $('[data-dr-count]', loader);
  const svg = $('.dr-loader__drips', loader), path = $('path', svg);
  const drips = makeDrips(innerWidth, { seed: 3, gap: [36, 100], r: [9, 20], len: [20, 90] });
  const dripH = Math.max(...drips.map((d) => d.len)) * 3.2 + 40;
  sizeSvg(svg, innerWidth, dripH);
  path.setAttribute('d', dripPath(innerWidth, drips, 1, 1));

  const counter = { v: 0 };
  const countTween = gsap.to(counter, { v: 90, duration: 1.6, ease: 'power2.out', onUpdate: () => (count.textContent = Math.round(counter.v)) });
  const img = (src) => new Promise((res) => { if (!src) return res(); const i = new Image(); i.onload = i.onerror = res; i.src = src; });
  const heroImg = $('[data-dr-char-img]')?.src;
  await Promise.race([Promise.all([displayFont, img(heroImg)]), wait(1200)]);

  firstMount();
  countTween.kill();
  await new Promise((res) => gsap.to(counter, { v: 100, duration: 0.2, ease: 'power2.out', onUpdate: () => (count.textContent = Math.round(counter.v)), onComplete: res }));

  unlockPage();
  try { sessionStorage.setItem('dr-loaded', '1'); localStorage.setItem('dr-loaded-at', String(Date.now())); } catch (e) { /* private mode */ }
  const s = { v: 1 };
  const H = innerHeight;
  gsap.timeline({ onComplete: () => { loader.remove(); ScrollTrigger.refresh(); } })
    .to([$('.dr-loader__logo', loader), $('.dr-loader__count', loader)], { y: -40, opacity: 0, duration: 0.35, ease: 'power2.in', stagger: 0.05 }, 0)
    .to($('.dr-loader__panel', loader), { y: -(H + dripH), duration: 1.05, ease: 'power3.inOut' }, 0.25)
    .to(svg, { y: -(H + dripH), duration: 1.05, ease: 'power3.inOut' }, 0.25)
    .to(s, { v: 3, duration: 0.8, ease: 'power2.in', onUpdate: () => path.setAttribute('d', dripPath(innerWidth, drips, 1, s.v)) }, 0.25)
    .add(() => { intro(); restMount(); }, 0.35);
}

/* "← Back" (snippets/drippy-back): the browser's back when the visitor came from this site, else the link (home, the pickles) */
document.addEventListener('click', (e) => {
  const a = e.target.closest?.('a[data-dr-back]');
  if (!a || e.metaKey || e.ctrlKey || e.shiftKey) return;
  let fromHere = false;
  try { fromHere = new URL(document.referrer).origin === location.origin; } catch (err) { /* no referrer */ }
  if (fromHere && history.length > 1) { e.preventDefault(); history.back(); }
});

/* hero "or see all 3 flavors": glide to the cards (Horizon scrolls .page-wrapper on desktop, so no native jump) */
document.addEventListener('click', (e) => {
  const a = e.target.closest?.('a.dr-hero__more');
  if (a && scrollToHash(new URL(a.href, location.href).hash)) e.preventDefault();
});

/* "Pick a pickle" in the empty cart drawer: on the home page, close the drawer and glide to the cards */
document.addEventListener('click', async (e) => {
  const a = e.target.closest?.('a.cart-items__empty-button');
  if (!a) return;
  const url = new URL(a.href, location.href);
  if (url.pathname !== location.pathname || url.hash.length < 2 || !document.getElementById(url.hash.slice(1))) return;
  e.preventDefault();
  // the drawer puts the page back where it was once it has closed, so scroll after that
  try { await Promise.race([cartDrawer()?.close?.(), wait(1000)]); } catch (err) { /* closed anyway */ }
  scrollToHash(url.hash);
});

/* "Complete the gang" Add buttons in the cart drawer (re-rendered by Horizon, so delegated) */
document.addEventListener('click', async (e) => {
  const b = e.target.closest?.('[data-dr-add]');
  if (!b || b.getAttribute('aria-busy') === 'true') return;
  e.preventDefault();
  b.setAttribute('aria-busy', 'true');
  try { await addToCart(b.dataset.drAdd, b.closest('li')?.querySelector('img') || b, b.dataset.img, 'drawer'); } finally { b.removeAttribute('aria-busy'); }
});

export { addToCart, money };
